import { useState } from "react";
import { useForm } from "react-hook-form";
import { Modal } from "components/modal";
import { CloseCircleGreyIcon } from "assets/website";
import { Input } from "components/website/form/input";
import { CustomSelect, initOptionType, type OptionType } from "components/website/form/select";
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { useEarlyAccess } from "hooks";
import type { EarlyAccessJobRole } from "hooks";
import { toast } from 'sonner';

interface EarlyAccessRequestModalProps {
  show: boolean;
  onClose: () => void;
}

interface EarlyAccessFormData {
  fullName: string;
  email: string;
}

const jobRoleOptions: OptionType[] = [
  { label: "Founder/CEO", value: "founder" },
  { label: "HR Lead", value: "hr_lead" },
  { label: "Hiring Manager", value: "hiring_manager" },
  { label: "Team Lead", value: "team_lead" },
  { label: "Trusted Advisor", value: "trusted_adviser" },
  { label: "Influencer", value: "influencer" },
];

const EarlyAccessRequestModal: React.FC<EarlyAccessRequestModalProps> = ({
  show,
  onClose,
}) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+234");
  const [jobRole, setJobRole] = useState<OptionType>(initOptionType);
  const [submitted, setSubmitted] = useState(false);

  const { mutate: requestEarlyAccess, isPending: loading } = useEarlyAccess();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EarlyAccessFormData>();

  const handlePhoneChange = (phone: string) => {
    setPhoneNumber(phone);
  };

  const handleRoleSelect = (option: OptionType) => {
    setJobRole(option);
  };

  const onSubmit = async (data: EarlyAccessFormData) => {
    setSubmitted(true);

    const hasFormErrors = Object.keys(errors).length > 0;
    const hasPhoneError = !phoneNumber || !countryCode;
    const hasJobRoleError = !jobRole.value;

    if (hasFormErrors || hasPhoneError || hasJobRoleError) {
      if (hasFormErrors) {
        toast.error('Please fill in all required fields correctly.');
      }
      if (hasPhoneError) {
        toast.error('Phone number is required.');
      }
      if (hasJobRoleError) {
        toast.error('Please select your job role.');
      }
      return;
    }

    const dialCode = countryCode.replace('+', '');
    const phoneWithoutCode = phoneNumber.startsWith(dialCode)
      ? phoneNumber.slice(dialCode.length)
      : phoneNumber;

    requestEarlyAccess(
      {
        full_name: data.fullName,
        email: data.email,
        job_role: jobRole.value as EarlyAccessJobRole,
        country_code: countryCode,
        phone_number: phoneWithoutCode,
      },
      {
        onSuccess: () => {
          reset();
          setJobRole(initOptionType);
          setPhoneNumber("");
          setCountryCode("+234");
          setSubmitted(false);
          onClose();
        },
      }
    );
  };

  return (
    <Modal show={show} close={onClose} variant="website">
      <div className="p-4 md:p-6 max-w-[450px] relative">
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#012169] font-display">
            Early Access
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 rounded-full transition-colors absolute top-0 right-0 text-gray-500 hover:text-gray-700"
          >
            <CloseCircleGreyIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-1">
          <Input
            type="text"
            name="fullName"
            register={register}
            required
            placeholder="Enter your full name"
            label="Full Name"
            validatorMessage={errors.fullName ? "Full name is required" : undefined}
            parentClassName="!mb-4"
          />

          <Input
            type="email"
            name="email"
            register={register}
            required
            placeholder="Enter your email address"
            label="Email address"
            validatorMessage={errors.email ? "Email is required" : undefined}
            parentClassName="!mb-4"
          />

          <CustomSelect
            name="jobRole"
            options={jobRoleOptions}
            value={jobRole}
            onChange={handleRoleSelect}
            placeholder="Select your role in the company"
            label="Job Role"
            validatorMessage={submitted && !jobRole.value ? "Job role is required" : ""}
            parentClassName="!mb-4"
          />

          <div className="!mb-4">
            <label className="block text-sm text-[#101323] font-medium mb-2">
              Phone Number
            </label>
            <PhoneInput
              defaultCountry="ng"
              value={phoneNumber}
              onChange={handlePhoneChange}
              inputClassName="!w-full !h-[44px] !pr-4 !pl-3 !border !border-[#dae0e6] !border-l-0 !rounded-r-md focus:!outline-none focus:!ring-0 focus:!border-[#dae0e6] !transition-colors !text-neutral-900 !text-sm placeholder:!text-neutral-400"
              countrySelectorStyleProps={{
                buttonClassName: "!h-[44px] !pl-3 !pr-2 !border !border-[#dae0e6] !border-r-0 !rounded-l-md !bg-white",
              }}
            />
            {submitted && !phoneNumber && (
              <small className="text-xs text-[#e74444] block mt-2">Phone number is required</small>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-[#012169] hover:bg-[#011550] disabled:bg-[#011550]/70 text-white font-semibold rounded-lg transition-colors mt-4"
          >
            {loading ? "Submitting..." : "Request Early Access"}
          </button>
        </form>
      </div>
    </Modal>
  );
};

export { EarlyAccessRequestModal };
